import { useMemo } from 'react';
import { rng } from './magic';

// Geometry ported from concept 07 (loader storyboards, 07-baxter-magic/index.html):
// a gravity well whose larger rings sit higher, so it recedes into the eye.
const W = 1280, H = 212, EYE = { x: 640, y: 118 }, RMAX = 540, RMIN = 46;
const cyOf = (r: number) => EYE.y - 78 * (r / RMAX);
const wellPt = (r: number, a: number) => [EYE.x + r * Math.cos(a), cyOf(r) + 0.3 * r * Math.sin(a)] as const;
const pts = (list: ReadonlyArray<readonly [number, number]>) => list.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');

/** Where Baxter sits, as a share of the band's height (the band keeps the SVG's height, see `slice`). */
export const SWIRL_EYE_Y = `${(EYE.y / H) * 100}%`;

export function reducedMotion() {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    && document.documentElement.getAttribute('data-motion') !== 'always';
}

/**
 * The loader's cyclone: ten log-spaced rings (inner ones amber and faster),
 * curved spokes, a warm halo, and bokeh motes that spiral from the rim into
 * the eye. Ambient, so its loops are linear; reduced motion parks everything.
 */
export function Swirl() {
  const { spokes, rings, motes, still } = useMemo(() => {
    const rand = rng(7);
    const spokes = Array.from({ length: 18 }, (_, k) => {
      const a = (k / 18) * Math.PI * 2;
      return pts(Array.from({ length: 13 }, (_, j) => wellPt(RMIN + (RMAX - RMIN) * (j / 12), a)));
    });
    const rings = Array.from({ length: 10 }, (_, k) => {
      const r = RMIN * Math.pow(RMAX / RMIN, k / 9);
      const speed = 26 * Math.sqrt(RMIN / r) * (k % 4 === 3 ? -0.6 : 1); // deg/s: inner rings turn faster
      const inner = k < 3;
      return {
        r, cy: cyOf(r), seconds: 360 / Math.abs(speed), reverse: speed < 0,
        stroke: inner ? 'hsl(43 90% 60%)' : 'hsl(240 5% 64.9%)',
        opacity: inner ? 0.28 - k * 0.05 : 0.2 - k * 0.012,
        dash: k % 3 === 1 ? `${Math.round(r * 0.9)} ${Math.round(r * 0.25)}` : k % 2 ? undefined : `${2 + k} ${6 + k * 2}`,
      };
    });
    const motes = Array.from({ length: 24 }, (_, i) => {
      const a0 = rand() * Math.PI * 2, r0 = RMAX * (0.62 + 0.36 * rand()), turns = 0.7 + 0.35 * rand();
      const path = Array.from({ length: 49 }, (_, j) => { const u = j / 48; return wellPt(r0 + (RMIN + 8 - r0) * u, a0 + u * turns * Math.PI * 2); });
      const seconds = 7 + 3 * rand();
      return {
        d: `M${pts(path).replaceAll(' ', ' L')}`, park: path[Math.round((0.25 + 0.5 * rand()) * 48)],
        r: 1.5 + rand() * 1.5, warm: i % 3 === 0, peak: 0.35 + 0.35 * rand(), seconds, begin: -rand() * seconds,
      };
    });
    return { spokes, rings, motes, still: reducedMotion() };
  }, []);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" className="absolute inset-0 size-full" aria-hidden="true">
      <defs>
        <linearGradient id="swirl-fade-x" x1="0" x2="1">
          <stop offset="0" stopColor="#fff" stopOpacity="0" /><stop offset=".18" stopColor="#fff" />
          <stop offset=".82" stopColor="#fff" /><stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="swirl-fade-y" x1="0" y1="0" x2="0" y2="1">
          <stop offset=".72" stopColor="#fff" /><stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <mask id="swirl-mx"><rect width={W} height={H} fill="url(#swirl-fade-x)" /></mask>
        <mask id="swirl-my"><rect width={W} height={H} fill="url(#swirl-fade-y)" /></mask>
        <radialGradient id="swirl-eye"><stop offset="0" stopColor="hsl(240 10% 2%)" /><stop offset=".7" stopColor="hsl(240 10% 3%)" /><stop offset="1" stopColor="hsl(43 90% 50%)" stopOpacity="0" /></radialGradient>
        <radialGradient id="swirl-halo"><stop offset="0" stopColor="hsl(43 96% 56%)" stopOpacity=".1" /><stop offset="1" stopColor="hsl(43 96% 56%)" stopOpacity="0" /></radialGradient>
        <radialGradient id="swirl-warm"><stop offset="0" stopColor="hsl(43 90% 80%)" stopOpacity=".9" /><stop offset=".7" stopColor="hsl(43 90% 70%)" stopOpacity="0" /></radialGradient>
        <radialGradient id="swirl-cool"><stop offset="0" stopColor="hsl(240 10% 90%)" stopOpacity=".8" /><stop offset=".7" stopColor="hsl(240 10% 90%)" stopOpacity="0" /></radialGradient>
      </defs>
      <g mask="url(#swirl-my)">
        <g mask="url(#swirl-mx)">
          {spokes.map((p, k) => <polyline key={k} points={p} fill="none" stroke="hsl(240 5% 64.9%)" strokeOpacity={0.07} />)}
          <ellipse cx={EYE.x} cy={EYE.y + 4} rx={150} ry={46} fill="url(#swirl-halo)" />
          {rings.map((ring) => (
            <g key={ring.r} transform={`translate(${EYE.x} ${ring.cy.toFixed(1)}) scale(1 0.3)`}>
              <g className="magic-spin" style={{ animationDuration: `${ring.seconds.toFixed(1)}s`, animationDirection: ring.reverse ? 'reverse' : 'normal', transformBox: 'fill-box', transformOrigin: 'center' }}>
                <circle r={ring.r} fill="none" stroke={ring.stroke} strokeOpacity={ring.opacity} vectorEffect="non-scaling-stroke" strokeDasharray={ring.dash} />
              </g>
            </g>
          ))}
          <ellipse cx={EYE.x} cy={EYE.y + 2} rx={42} ry={13} fill="url(#swirl-eye)" />
          {motes.map((m, i) => (still ? (
            <circle key={i} cx={m.park[0]} cy={m.park[1]} r={m.r} fill={`url(#swirl-${m.warm ? 'warm' : 'cool'})`} opacity={m.peak * 0.7} />
          ) : (
            <circle key={i} r={m.r} fill={`url(#swirl-${m.warm ? 'warm' : 'cool'})`} opacity={0}>
              <animateMotion path={m.d} dur={`${m.seconds.toFixed(2)}s`} begin={`${m.begin.toFixed(2)}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values={`0;${m.peak.toFixed(2)};${m.peak.toFixed(2)};0`} keyTimes="0;0.25;0.7;1"
                dur={`${m.seconds.toFixed(2)}s`} begin={`${m.begin.toFixed(2)}s`} repeatCount="indefinite" />
            </circle>
          )))}
        </g>
      </g>
    </svg>
  );
}
